import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { APIURL } from '../../../config';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    IconButton,
    Snackbar,
    Alert,
    Chip,
    Card,
    CardContent,
    Autocomplete
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const CustomerPreferences = ({ employee, comp_id }) => {
    const [preferences, setPreferences] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [preferenceLevel, setPreferenceLevel] = useState('');
    const [currentPreference, setCurrentPreference] = useState(null);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState('success');
    const [alertMessage, setAlertMessage] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [preferenceToDelete, setPreferenceToDelete] = useState(null);
    const [pageSize, setPageSize] = useState(10);

    const preferenceLevels = [
        { value: 1, label: 'Low' },
        { value: 2, label: 'Medium' },
        { value: 3, label: 'High' },
        { value: 4, label: 'Very High' },
        { value: 5, label: 'Preferred' }
    ];

    useEffect(() => {
        if (employee?.emp_id) {
            fetchPreferences();
            fetchCustomers();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employee]);

    const fetchPreferences = async () => {
        try {
            setLoading(true);
            const response = await axios.get(APIURL + '/customers/employeePreference', {
                params: { employee_id: employee.emp_id }
            });
            setPreferences(response.data.preferences || []);
        } catch (error) {
            console.error('Error fetching preferences:', error);
            showAlert('error', 'Failed to load customer preferences');
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomers = async () => {
        try {
            const response = await axios.get(APIURL + '/customers', {
                params: { comp_id: comp_id }
            });
            // Filter to only show active customers
            const activeCustomers = (response.data || []).filter(customer => customer.cus_status === 'Active');
            setCustomers(activeCustomers);
        } catch (error) {
            console.error('Error fetching customers:', error);
            showAlert('error', 'Failed to load customers');
        }
    };

    const handleOpenDialog = (preference = null) => {
        if (preference) {
            setEditMode(true);
            setCurrentPreference(preference);
            // Create customer object from preference data for edit mode
            const customer = {
                cus_id: preference.customer_id,
                customer_name: preference.cus_name
            };
            setSelectedCustomer(customer);
            setPreferenceLevel(preference.preference_level || '');
        } else {
            setEditMode(false);
            setCurrentPreference(null);
            setSelectedCustomer(null);
            setPreferenceLevel('');
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditMode(false);
        setCurrentPreference(null);
        setSelectedCustomer(null);
        setPreferenceLevel('');
    };

    const handleSavePreference = async () => {
        try {
            if (!selectedCustomer) {
                showAlert('error', 'Please select a customer');
                return;
            }

            if (editMode) {
                // Update existing preference
                await axios.put(APIURL + '/customers/employeePreference', {
                    preference_id: currentPreference.preference_id,
                    preference_level: preferenceLevel || null
                });
                showAlert('success', 'Preference updated successfully');
            } else {
                // Add new preference
                await axios.post(APIURL + '/customers/employeePreference', {
                    employee_id: employee.emp_id,
                    customer_id: selectedCustomer.id,
                    preference_level: preferenceLevel || null
                });
                showAlert('success', 'Preference added successfully');
            }

            handleCloseDialog();
            fetchPreferences();
        } catch (error) {
            console.error('Error saving preference:', error);
            if (error.response?.status === 409) {
                showAlert('error', 'This customer preference already exists');
            } else {
                showAlert('error', 'Failed to save preference');
            }
        }
    };

    const handleDeleteClick = (preference) => {
        setPreferenceToDelete(preference);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await axios.delete(APIURL + '/customers/employeePreference', {
                data: {
                    preference_id: preferenceToDelete.preference_id
                }
            });
            showAlert('success', 'Preference deleted successfully');
            setDeleteDialogOpen(false);
            setPreferenceToDelete(null);
            fetchPreferences();
        } catch (error) {
            console.error('Error deleting preference:', error);
            showAlert('error', 'Failed to delete preference');
        }
    };

    const showAlert = (severity, message) => {
        setAlertSeverity(severity);
        setAlertMessage(message);
        setAlertOpen(true);
    };

    const getPreferenceLevelLabel = (level) => {
        const pref = preferenceLevels.find(p => p.value === level);
        return pref ? pref.label : 'Not Set';
    };

    const getPreferenceLevelColor = (level) => {
        if (!level) return 'default';
        if (level >= 5) return 'success';
        if (level >= 3) return 'primary';
        return 'default';
    };

    const columns = [
        {
            field: 'cus_name',
            headerName: 'CUSTOMER',
            flex: 1,
            minWidth: 200
        },
        {
            field: 'preference_level',
            headerName: 'PREFERENCE LEVEL',
            width: 180,
            renderCell: (params) => (
                <Chip
                    label={getPreferenceLevelLabel(params.value)}
                    color={getPreferenceLevelColor(params.value)}
                    size="small"
                />
            )
        },
        {
            field: 'created_at',
            headerName: 'CREATED',
            width: 130,
            valueFormatter: (value) => {
                return value ? new Date(value).toLocaleDateString() : '-';
            }
        },
        {
            field: 'actions',
            headerName: 'ACTIONS',
            width: 120,
            renderCell: (params) => (
                <Box>
                    <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(params.row)}
                        color="primary"
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(params.row)}
                        color="error"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            )
        }
    ];

    const rows = preferences.map((pref) => ({
        id: pref.preference_id,
        ...pref
    }));

    // Filter out customers that already have preferences
    const availableCustomers = customers.filter(
        customer => !preferences.some(pref => pref.customer_id === customer.cus_id)
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                    Customer Preferences for {employee?.emp_first_name} {employee?.emp_last_name}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    disabled={availableCustomers.length === 0}
                >
                    Add Preference
                </Button>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 3, mb: 3 }}>
                Set customer preferences to prioritize this employee when assigning tasks. Higher preference levels will make this employee appear higher in the assignment list for the selected customer.
            </Typography>

            {preferences.length === 0 && !loading ? (
                <Card>
                    <CardContent>
                        <Typography variant="body1" color="textSecondary" align="center">
                            No customer preferences set for this employee.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <DataGridPro
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    pageSizeOptions={[10, 25, 50]}
                    pagination
                    autoHeight
                    disableSelectionOnClick
                    initialState={{ density: 'compact' }}
                />
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editMode ? 'Edit Customer Preference' : 'Add Customer Preference'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Autocomplete
                            value={selectedCustomer}
                            onChange={(event, newValue) => setSelectedCustomer(newValue)}
                            options={editMode ? (selectedCustomer ? [selectedCustomer] : []) : availableCustomers}
                            getOptionLabel={(option) => option.customer_name || ''}
                            getOptionKey={(option) => option.cus_id}
                            disabled={editMode}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Customer"
                                    required
                                    placeholder="Search for a customer..."
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.cus_id === value.cus_id}
                            fullWidth
                        />

                        <TextField
                            select
                            label="Preference Level (Optional)"
                            value={preferenceLevel}
                            onChange={(e) => setPreferenceLevel(e.target.value)}
                            fullWidth
                            helperText="Leave empty if no specific level needed"
                        >
                            <MenuItem value="">None</MenuItem>
                            {preferenceLevels.map((level) => (
                                <MenuItem key={level.value} value={level.value}>
                                    {level.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSavePreference} variant="contained">
                        {editMode ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove the preference for{' '}
                        <strong>{preferenceToDelete?.customer_name}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Alert Snackbar */}
            <Snackbar
                open={alertOpen}
                autoHideDuration={5000}
                onClose={() => setAlertOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setAlertOpen(false)}
                    severity={alertSeverity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CustomerPreferences;
